import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import { format, parseISO } from 'date-fns';
import type { OpenPotDto } from '@billda/api';
import { routeBuilders } from '../../../routes';
import { formatCurrency } from '../../../shared/utils/currency';
import { TreeConnectorBox } from '../../../shared/components/grouped-view';
import { PotStatusBadge } from '../../finance/components/PotStatusBadge';
import { useGetOpenPots } from '../hooks/useGetOpenPots';
import { formatWeekLabel } from '../utils/periodFormatting';
import {
  ACTION_STATUSES,
  filterByStatuses,
  getNextAction,
  groupOpenPotsByWeek,
  type ActionPotStatus,
  type OpenPotsWeekGroup,
} from '../utils/groupOpenPotsByWeek';

const STATUS_PARAM = 'potStatus';
const ALL_OPTION = 'all';

export function OpenPotsWidget() {
  const navigate = useNavigate();
  const { data, isLoading } = useGetOpenPots();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedStatuses = parseStatuses(searchParams.get(STATUS_PARAM));

  const weeks = useMemo(() => {
    const pots = data?.pots ?? [];
    return groupOpenPotsByWeek(filterByStatuses(pots, selectedStatuses));
  }, [data, selectedStatuses]);

  const handleStatusesChange = (
    event: SelectChangeEvent<ActionPotStatus[]>
  ) => {
    const value = event.target.value;
    const raw = (
      typeof value === 'string' ? value.split(',') : value
    ) as string[];
    // The "All" option toggles the whole set: select everything unless it's
    // already all selected, in which case clear it.
    const next: ActionPotStatus[] = raw.includes(ALL_OPTION)
      ? selectedStatuses.length === ACTION_STATUSES.length
        ? []
        : [...ACTION_STATUSES]
      : (raw.filter(s =>
          (ACTION_STATUSES as string[]).includes(s)
        ) as ActionPotStatus[]);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (next.length === ACTION_STATUSES.length) {
        params.delete(STATUS_PARAM);
      } else {
        params.set(STATUS_PARAM, next.join(','));
      }
      return params;
    });
  };

  const handleRowClick = (potId: string) => {
    navigate(routeBuilders.finance.potDetail(potId));
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const totalCount = data?.totalCount ?? 0;

  return (
    <Card data-testid="open-pots-widget" sx={{ flex: 1, minWidth: 0 }}>
      <CardContent
        sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Open Pots
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.secondary"
            data-testid="open-pots-total-count"
          >
            {totalCount}
          </Typography>
        </Box>

        <FormControl
          size="small"
          sx={{ alignSelf: 'flex-start', minWidth: 200 }}
        >
          <Select
            multiple
            displayEmpty
            value={selectedStatuses}
            onChange={handleStatusesChange}
            renderValue={selected => renderStatusValue(selected)}
            data-testid="open-pots-status-filter"
            sx={{ '& .MuiSelect-select': { py: 0.75 } }}
          >
            <MenuItem
              value={ALL_OPTION}
              data-testid="open-pots-status-option-all"
            >
              <Checkbox
                size="small"
                checked={selectedStatuses.length === ACTION_STATUSES.length}
                indeterminate={
                  selectedStatuses.length > 0 &&
                  selectedStatuses.length < ACTION_STATUSES.length
                }
              />
              <ListItemText primary="All statuses" />
            </MenuItem>
            {ACTION_STATUSES.map(status => (
              <MenuItem
                key={status}
                value={status}
                data-testid={`open-pots-status-option-${status}`}
              >
                <Checkbox
                  size="small"
                  checked={selectedStatuses.includes(status)}
                />
                <ListItemText primary={getStatusPickerLabel(status)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{ maxHeight: 420, overflowY: 'auto' }}
          data-testid="open-pots-list"
        >
          {weeks.length === 0 ? (
            <EmptyState />
          ) : (
            <Stack spacing={2}>
              {weeks.map(week => (
                <WeekSection
                  key={`${week.periodYear}-${week.periodWeekNumber}`}
                  week={week}
                  onRowClick={handleRowClick}
                />
              ))}
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function parseStatuses(value: string | null): ActionPotStatus[] {
  if (value === null) {
    return [...ACTION_STATUSES];
  }
  return value
    .split(',')
    .filter((s): s is ActionPotStatus =>
      (ACTION_STATUSES as string[]).includes(s)
    );
}

function renderStatusValue(selected: ActionPotStatus[]): string {
  if (selected.length === ACTION_STATUSES.length) {
    return 'All statuses';
  }
  if (selected.length === 0) {
    return 'No statuses';
  }
  return selected.map(getStatusPickerLabel).join(', ');
}

function getStatusPickerLabel(status: ActionPotStatus): string {
  if (status === 'MissedSubmitDeadline') {
    return 'Missed Deadline';
  }
  if (status === 'MissedVerificationDeadline') {
    return 'Missed Verification';
  }
  return status;
}

interface WeekSectionProps {
  week: OpenPotsWeekGroup;
  onRowClick: (potId: string) => void;
}

function WeekSection({ week, onRowClick }: WeekSectionProps) {
  const weekLabel = formatWeekLabel([
    {
      year: week.periodYear,
      weekNumber: week.periodWeekNumber,
      periodStart: week.weekCommencing,
      periodEnd: week.weekCommencing,
    },
  ]);
  const weekCommencingLabel = format(parseISO(week.weekCommencing), 'd MMM');

  return (
    <Box
      data-testid={`open-pots-week-${week.periodYear}-${week.periodWeekNumber}`}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          py: 0.5,
          zIndex: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {weekLabel} · w/c {weekCommencingLabel}
        </Typography>
      </Box>
      <Box>
        {week.pots.map((pot, index) => (
          <PotRow
            key={pot.potId}
            pot={pot}
            isLast={index === week.pots.length - 1}
            onClick={onRowClick}
          />
        ))}
      </Box>
    </Box>
  );
}

interface PotRowProps {
  pot: OpenPotDto;
  isLast: boolean;
  onClick: (potId: string) => void;
}

function PotRow({ pot, isLast, onClick }: PotRowProps) {
  return (
    <ButtonBase
      onClick={() => onClick(pot.potId)}
      data-testid={`open-pot-row-${pot.potId}`}
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ pl: 1 }}>
        <TreeConnectorBox isLast={isLast}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              pr: 1,
              minHeight: 40,
            }}
          >
            <PotStatusBadge status={pot.status} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                {pot.isGang ? (
                  <GroupsIcon
                    sx={{
                      fontSize: 16,
                      color: 'text.secondary',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <PersonIcon
                    sx={{
                      fontSize: 16,
                      color: 'text.secondary',
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography variant="body2" fontWeight={600} noWrap>
                  {pot.claimantName || 'Unnamed pot'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" noWrap>
                {getNextAction(pot.status)}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ minWidth: 72, textAlign: 'right', flexShrink: 0 }}
            >
              {formatCurrency(pot.grossValue)}
            </Typography>
          </Box>
        </TreeConnectorBox>
      </Box>
    </ButtonBase>
  );
}

function EmptyState() {
  return (
    <Box sx={{ py: 4, textAlign: 'center' }} data-testid="open-pots-empty">
      <Typography variant="body2" color="text.secondary">
        No pots need action
      </Typography>
    </Box>
  );
}

function LoadingSkeleton() {
  return (
    <Card data-testid="open-pots-widget-loading" sx={{ flex: 1, minWidth: 0 }}>
      <CardContent sx={{ p: 3 }}>
        <Skeleton width={220} height={24} sx={{ mb: 2 }} />
        <Skeleton width="100%" height={32} sx={{ mb: 2 }} />
        <Stack spacing={1}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={32} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
